/* eslint-disable */
import { useEffect, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import '../../b2bPage/b2bPage.css';
import '../../b2bPage/b2bGammaTheme.module.css';
import ErrorPage from '../../errorPage/ErrorPage';
import { Trans, useTranslation } from 'react-i18next';
import { getPortInServiceMessage, getStationOfflineMessage, isStationInService } from '../../../utils/utils';
import { Button } from '@ui';
import { StationMediaCarousel } from '../../../components/stationMediaCarousel/StationMediaCarousel';
import Box from '@mui/material/Box';
import Spinner from '../../../components/loaders/globalSpinner/Spinner';
import { api, useGetStationStatusQuery } from '../../../redux/api';
import { useGetClientId } from '../../../hookServices/clientId';
import RestartStationButton from '../RestartStationButton';
import StartPageTopOnlineStatus from '../StartPageTopOnlineStatus';
import StartPageStationNotes from '../StartPageStationNotes';
import StartManyLocationHeader from '../../startManyPage/StartManyLocationHeader';
import ChargingPortCard from '../ChargingPortCard';
import OpenMapsConfirmModal from '../../../components/OpenMapsConfirmModal';
import OutageScheduleButton from '../../../components/outageSchedule/OutageScheduleButton';
import ServiceRequestModal from '../ServiceRequestModal';
import ReactGA from 'react-ga';
import { TELEGRAM_BOT } from '../../../environment';

export default function PrivateRoute({ children, fingerPrint }) {
  const { station, stationNumber, isFetchingStation, isLoadingStation, isErrorStation } = useOutletContext();
  const { clientId } = useGetClientId();
  const [mapsModalOpen, setMapsModalOpen] = useState(false);
  const [serviceRequestModalOpen, setServiceRequestModalOpen] = useState(false);

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = api.useGetHistoryDataQuery(clientId, { skip: !clientId });
  const isHistoryReady = !!clientId && !isHistoryLoading && (historyData !== undefined || isHistoryError);

  const {
    data: stationStatus,
    isLoading: isLoadingStationStatus,
    isFetching: isFetchingStationStatus,
    isError: isErrorStationStatus,
  } = useGetStationStatusQuery({ stationNumber }, { skip: !clientId || !stationNumber });

  const { t } = useTranslation();
  const isOfflineStation = station?.online === false;

  useEffect(() => {
    if (!isOfflineStation) {
      return undefined;
    }
    document.body.classList.add('b2b-route');
    const layout = document.getElementById('layout');
    layout?.classList.add('b2b-route-layout');
    return () => {
      document.body.classList.remove('b2b-route');
      layout?.classList.remove('b2b-route-layout');
    };
  }, [isOfflineStation]);

  const getOfflineMessage = () => getStationOfflineMessage(station, t);

  // Redirect to promoLink if present in station name
  if (station?.name?.includes('promoLink:')) {
    const match = station.name.match(/promoLink:([^\s]+)/);
    if (match && match[1]) {
      const promoLink = match[1].replace(/ocppPortNumber:(?:\d+|unknown)$/, '');
      window.location.replace(promoLink);
      return null;
    }
  }

  const lastJobState = stationStatus?.lastJob?.state;
  // Balance start creates a CREATED job before it becomes WAITING/IN_PROGRESS.
  // Without handling CREATED (and without a default branch), this route rendered blank.
  const hasActiveOrStartingJob =
    lastJobState === 'IN_PROGRESS' || lastJobState === 'WAITING' || lastJobState === 'CREATED';

  if (isLoadingStationStatus || isLoadingStation) {
    return <Spinner pageCentered />;
  }

  if (hasActiveOrStartingJob) {
    return <Navigate to={`/charging?station=${stationNumber}`} />;
  }

  if (isErrorStationStatus || isErrorStation) {
    return <ErrorPage centerOnScreen errorHeader={t('errorDevHeader')} errorBody={t('errorDevBody')} />;
  }

  if (station?.online === false) {
    const inService = isStationInService(station);
    const inServiceBody = getPortInServiceMessage(station, t);
    return (
      <ErrorPage
        errorHeader={inService ? undefined : t('stationOffline')}
        errorBody={inService ? inServiceBody : getOfflineMessage()}
        hideContactUs={inService}
        isInServicePage={inService}
        stationNumber={stationNumber}
        afterMessageChildren={
          inService ? undefined : (
            <Button
              type="button"
              variant="outline"
              className="error-page-notify-btn"
              onClick={() => {
                ReactGA.event({
                  category: 'contacts',
                  action: 'notify when online',
                });
                window.open(
                  `https://t.me/${TELEGRAM_BOT}?start=${encodeURIComponent(clientId || 'web')}-notify_${stationNumber || ''}`,
                  '_blank'
                );
              }}
            >
              <span>{t('notifyWhenOnline')}</span>
            </Button>
          )
        }
        belowMessageChildren={
          inService ? undefined : (
            <Box className="error-page-offline-extras" sx={{ width: '100%' }}>
              <StartPageStationNotes station={station} />
              <StationMediaCarousel
                stationNumber={stationNumber}
                protocolType={station?.protocolType}
                webcamUrl={station?.webcamUrl}
              />
            </Box>
          )
        }
        footerChildren={
          inService ? undefined : (
            <>
              <Button
                type="button"
                variant="outline"
                className="error-page-action-btn"
                style={{ marginTop: '14px' }}
                onClick={() => setMapsModalOpen(true)}
              >
                <span>{t('route')}</span>
              </Button>
              <OutageScheduleButton
                latitude={station?.latitude}
                longitude={station?.longitude}
                stationNumber={stationNumber}
                className="error-page-action-btn"
                style={{ marginTop: '14px' }}
                labelStyle={null}
              />
              <Box className="start-page-footer-actions">
                <Button
                  type="button"
                  variant="outline"
                  className="error-page-action-btn"
                  onClick={() => setServiceRequestModalOpen(true)}
                >
                  <span>{t('serviceRequest')}</span>
                </Button>
                <RestartStationButton
                  station={station}
                  stationNumber={stationNumber}
                  historyData={historyData}
                  isHistoryReady={isHistoryReady}
                />
              </Box>
              <OpenMapsConfirmModal
                modalOpen={mapsModalOpen}
                setModalOpen={setMapsModalOpen}
                latitude={station?.latitude}
                longitude={station?.longitude}
                stationNumber={stationNumber}
              />
              <ServiceRequestModal
                modalOpen={serviceRequestModalOpen}
                setModalOpen={setServiceRequestModalOpen}
                station={station}
                stationNumber={stationNumber}
                clientId={clientId}
              />
            </>
          )
        }
      >
        <div className="start-page-root start-page-root--with-map" style={{ width: '100%' }}>
          <StartManyLocationHeader stations={station ? [station] : []} showShare showPrintOrder />
          <div id="startPage" style={{ width: '100%' }}>
            {!(station?.latitude != null && station?.longitude != null) ? (
              <StartPageTopOnlineStatus station={station} />
            ) : null}
            <Box
              className="error-page-offline-port"
              sx={{ m: '4px auto 0', px: '7%', maxWidth: '600px', width: '100%', boxSizing: 'border-box' }}
            >
              <ChargingPortCard
                station={station}
                stationNumber={stationNumber}
                style={{ width: '100%', marginBlock: '4px 0' }}
              />
            </Box>
          </div>
        </div>
      </ErrorPage>
    );
  }

  // DONE / FAILED / CANCELED / no last job — show start UI
  return children;
}
